import React, { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
} from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  CREATE_AMCR_FIND_MUTATION,
  GET_AMCR_FIND_PREVIEWS_QUERY,
  GET_AMCR_VOCABULARIES_QUERY,
  GET_MY_AMCR_ACCOUNT_QUERY,
} from '../../gql';
import { Button } from '../design-system';
import {
  AMCR_FIND_PREVIEW_FORM_ID,
  AmcrFindPreview,
  AmcrFindRecordEdits,
  AmcrFindRecordPreview,
} from './AmcrFindPreview';
import { AmcrSignInForm } from './AmcrSignInForm';
import { AmcrErrorDetail, getAmcrProblemMessage } from './messages';

export type { AmcrErrorDetail };
export { getAmcrProblemMessage };
export { amcrPreviewMessages } from './messages';
export { isCoordinate, toNumber, toPoint } from './coordinates';
export { AmcrPasPhotoDialog } from './AmcrPasPhotoDialog';
export type { AmcrPasFile } from './AmcrPasPhotoDialog';
export { AmcrPasSubmitDialog } from './AmcrPasSubmitDialog';

export interface AmcrFindPreviewResult {
  findId: string;
  record: AmcrFindRecordPreview | null;
  projectIds: string[];
  errors: AmcrErrorDetail[];
}

interface AmcrPasCreateDialogProps {
  findId: string;
  onClose: () => void;
  onCreated: (amcrId: string) => void;
}

const SIGN_IN_REQUIRED_PATH = 'amcr.unauthorized';
const UNCERTAIN_PATH = 'amcr.uncertain';

const contentStyles = { minWidth: { sm: 560 } };
const loadingStyles = { py: 4, alignItems: 'center' };
const errorListStyles = { m: 0, pl: 2 };
const hiddenStyles = { display: 'none' };
const NO_FINDER_NAMES: string[] = [];

const AmcrPasCreateDialog = ({
  findId,
  onClose,
  onCreated,
}: AmcrPasCreateDialogProps) => {
  const intl = useIntl();
  const [isSignInRequired, setIsSignInRequired] = useState(false);
  const [pendingEdits, setPendingEdits] = useState<AmcrFindRecordEdits | null>(
    null
  );
  const [createErrors, setCreateErrors] = useState<AmcrErrorDetail[]>([]);
  const findIds = useMemo(() => [findId], [findId]);

  const accountQuery = useQuery(GET_MY_AMCR_ACCOUNT_QUERY, {
    fetchPolicy: 'network-only',
  });
  const account = accountQuery.data?.getMyAmcrAccount;
  const hasValidAccount = Boolean(account && !account.expired);
  const isSignedIn = hasValidAccount && !isSignInRequired;

  // Stays loaded while re-signing in so the user's edits are kept.
  const previewQuery = useQuery(GET_AMCR_FIND_PREVIEWS_QUERY, {
    variables: { findIds },
    fetchPolicy: 'network-only',
    skip: !hasValidAccount,
  });
  const preview: AmcrFindPreviewResult | undefined =
    previewQuery.data?.getAmcrFindPreviews?.[0];

  const vocabulariesQuery = useQuery(GET_AMCR_VOCABULARIES_QUERY, {
    fetchPolicy: 'cache-first',
    skip: !hasValidAccount,
  });
  const vocabularies = vocabulariesQuery.data?.getAmcrVocabularies;

  const [createAmcrFind, { loading: creating }] = useMutation(
    CREATE_AMCR_FIND_MUTATION
  );

  const handleClose = () => {
    if (!creating) {
      onClose();
    }
  };

  const handleCreate = async (edits: AmcrFindRecordEdits) => {
    setCreateErrors([]);

    try {
      const { data } = await createAmcrFind({
        variables: { findId, record: edits },
      });
      const result = data?.createAmcrFind;

      if (result?.find?.amcrId) {
        onCreated(result.find.amcrId);
        onClose();
        return;
      }

      const resultErrors: AmcrErrorDetail[] = result?.errors || [];

      if (resultErrors.some(({ path }) => path === SIGN_IN_REQUIRED_PATH)) {
        setPendingEdits(edits);
        setIsSignInRequired(true);
        return;
      }

      setCreateErrors(resultErrors);
    } catch {
      setCreateErrors([
        {
          path: 'amcr',
          error: intl.formatMessage({
            id: 'amcrPas.create.failed',
            defaultMessage: 'The find could not be sent to PAS.',
          }),
        },
      ]);
    }
  };

  const handleSignedIn = () => {
    setIsSignInRequired(false);

    if (pendingEdits) {
      setPendingEdits(null);
      handleCreate(pendingEdits);
    }
  };

  const isUncertain = createErrors.some(({ path }) => path === UNCERTAIN_PATH);
  const errors = [...(preview?.errors || []), ...createErrors];
  const isPreviewLoading =
    (previewQuery.loading && !preview) ||
    (vocabulariesQuery.loading && !vocabularies);
  const isLoading =
    accountQuery.loading || (hasValidAccount && isPreviewLoading);
  const canCreate = Boolean(preview?.record) && !creating && !isUncertain;

  const renderPreview = () => (
    <Box sx={isSignedIn ? undefined : hiddenStyles}>
      <DialogContent sx={contentStyles}>
        <Stack gap={2}>
          {preview?.record && vocabularies && (
            <AmcrFindPreview
              record={preview.record}
              projectIds={preview.projectIds}
              finderNames={account?.finderNames ?? NO_FINDER_NAMES}
              vocabularies={vocabularies}
              disabled={creating || isUncertain}
              onSubmit={handleCreate}
            />
          )}
          {(previewQuery.error || vocabulariesQuery.error) && (
            <Alert severity="error" variant="outlined">
              <FormattedMessage
                id="amcrPas.preview.failed"
                defaultMessage="The PAS preview could not be loaded."
              />
            </Alert>
          )}
          {errors.length > 0 && (
            <Alert severity="error" variant="outlined">
              <Stack component="ul" sx={errorListStyles}>
                {errors.map((detail) => (
                  <li key={`${detail.path}:${detail.error}`}>
                    {getAmcrProblemMessage(intl, detail)}
                  </li>
                ))}
              </Stack>
              {isUncertain && account?.findsUrl && (
                <Link
                  href={account.findsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FormattedMessage
                    id="amcrPas.create.openFinds"
                    defaultMessage="Open my finds in AMCR-PAS"
                  />
                </Link>
              )}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button tone="neutral" disabled={creating} onClick={handleClose}>
          <FormattedMessage id="general.cancel" defaultMessage="Cancel" />
        </Button>
        <Button
          type="submit"
          form={AMCR_FIND_PREVIEW_FORM_ID}
          disabled={!canCreate}
        >
          <FormattedMessage
            id="amcrPas.create.submit"
            defaultMessage="Create"
          />
        </Button>
      </DialogActions>
    </Box>
  );

  return (
    <Dialog open maxWidth="md" onClose={handleClose}>
      <DialogTitle>
        <FormattedMessage
          id="amcrPas.create.title"
          defaultMessage="Create in PAS"
        />
      </DialogTitle>
      {isLoading && (
        <DialogContent sx={contentStyles}>
          <Stack sx={loadingStyles}>
            <CircularProgress size={32} />
          </Stack>
        </DialogContent>
      )}
      {!isLoading && !isSignedIn && (
        <AmcrSignInForm
          isSessionExpired={Boolean(account) || isSignInRequired}
          onCancel={handleClose}
          onSignedIn={handleSignedIn}
        />
      )}
      {!isLoading && hasValidAccount && renderPreview()}
    </Dialog>
  );
};

export { AmcrPasCreateDialog };
